import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ViewChild
} from '@angular/core';
import { WorkshopJobListStoreService } from '../workshop-job-list-store.service';
import {
  combineLatestWith,
  debounceTime,
  map,
  switchMap,
  take,
  takeUntil
} from 'rxjs/operators';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { WorkshopJobListItem } from '../../../models/workshop-job-list-item';
import { MatSort, Sort, MatSortHeader } from '@angular/material/sort';
import { ConfigService } from '../../../shared/services/config.service';
import { BehaviorSubject, merge, Observable, of, Subscription } from 'rxjs';
import { WORKSHOP } from '../../../../testIds';
import { WorkshopJobService } from '../../../shared/services/workshop-job.service';
import { DataSource } from '@angular/cdk/collections';
import { MomentModule } from 'ngx-moment';
import { WorkshopJobStatusLabelPipe } from '../../../shared/pipes/workshop-job-status-label.pipe';
import { UserIdToNamePipe } from '../../../shared/pipes/user-id-to-name.pipe';
import { LocationNameFromIdPipe } from '../../../shared/pipes/location-name-from-id.pipe';
import { NgIf, AsyncPipe } from '@angular/common';
import { RouterLink } from '@angular/router';
import {
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow
} from '@angular/material/table';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatIcon } from '@angular/material/icon';

const allColumns = [
  'id',
  'customer',
  'equipment',
  'type',
  'assigned_to',
  'status',
  'booked_in_location',
  'booked_in_at',
  'actions'
];

@Component({
  selector: 'app-workshop-job-list',
  templateUrl: './workshop-job-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    MatCard,
    MatCardContent,
    MatIcon,
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    RouterLink,
    NgIf,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatPaginator,
    AsyncPipe,
    LocationNameFromIdPipe,
    UserIdToNamePipe,
    WorkshopJobStatusLabelPipe,
    MomentModule
  ]
})
export class WorkshopJobListComponent implements AfterViewInit {
  tableColumns$: Observable<string[]>;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  testIds = WORKSHOP;
  dateFormat = ConfigService.dates.dateFormat;

  dataSource = new WorkshopJobListDataSource(
    this.workshopJobService,
    this.workshopJobListStore
  );

  constructor(
    private workshopJobListStore: WorkshopJobListStoreService,
    private configService: ConfigService,
    private store: WorkshopJobListStoreService,
    private workshopJobService: WorkshopJobService
  ) {
    this.tableColumns$ = this.configService.locations.pipe(
      map((locations) => locations.length > 1),
      map((multiLocation) =>
        multiLocation
          ? allColumns
          : allColumns.filter((col) => col !== 'booked_in_location')
      )
    );
  }

  ngAfterViewInit(): void {
    this.dataSource.setPaginator(this.paginator);
    this.dataSource.setSort(this.sort);
  }

  excelExport() {
    this.store.filterQuery$
      .pipe(
        take(1),
        takeUntil(this.store.destroy$),
        switchMap((filter) => {
          return this.workshopJobService.getJobListItems(
            filter.search,
            filter.status,
            filter.customer,
            0,
            10_000,
            { active: this.sort.active, direction: this.sort.direction },
            true
          );
        })
      )
      .subscribe((data) => {
        const blob = new Blob([data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'workshop-jobs.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      });
  }
}

class WorkshopJobListDataSource extends DataSource<WorkshopJobListItem> {
  private _myRenderData = new BehaviorSubject<WorkshopJobListItem[]>([]);
  private _renderChangesSubscription: Subscription | null = null;

  private matPaginator: MatPaginator;
  private matSort: MatSort;

  constructor(
    private workshopJobService: WorkshopJobService,
    private store: WorkshopJobListStoreService
  ) {
    super();
    this._updateChangeSubscription();
  }

  connect() {
    if (!this._renderChangesSubscription) {
      this._updateChangeSubscription();
    }

    return this._myRenderData;
  }

  /**
   * Used by the MatTable. Called when it disconnects from the data source.
   * @docs-private
   */
  disconnect() {
    this._renderChangesSubscription?.unsubscribe();
    this._renderChangesSubscription = null;
  }

  setPaginator(paginator: MatPaginator) {
    this.matPaginator = paginator;
    this._updateChangeSubscription();
  }

  setSort(sort: MatSort) {
    this.matSort = sort;
    this._updateChangeSubscription();
  }

  /**
   * Subscribe to changes that should trigger an update to the table's rendered rows. When the
   * changes occur, process the current state of the filter, sort, and pagination along with
   * the provided base data and send it to the table for rendering.
   */
  _updateChangeSubscription() {
    // Sorting and/or pagination should be watched if sort and/or paginator are provided.
    // The events should emit whenever the component emits a change or initializes, or if no
    // component is provided, a stream with just a null event should be provided.
    // The `sortChange` and `pageChange` acts as a signal to the combineLatests below so that the
    // pipeline can progress to the next step. Note that the value from these streams are not used,
    // they purely act as a signal to progress in the pipeline.
    const sortChange: Observable<Sort | null> = this.matSort
      ? merge(
          this.matSort.sortChange,
          this.matSort.initialized.pipe(map(() => null))
        )
      : of(null);
    const pageChange: Observable<PageEvent | null> = this.matPaginator
      ? (merge(
          this.matPaginator.page,
          this.matPaginator.initialized.pipe(
            map(() => ({
              pageIndex: this.matPaginator.pageIndex,
              pageSize: this.matPaginator.pageSize,
              length: 0
            }))
          )
        ) as Observable<PageEvent>)
      : of(null);

    const paginatedData = this.store.filterQuery$.pipe(
      debounceTime(200),
      combineLatestWith(sortChange, pageChange),
      switchMap(([filter, sort, page]) => {
        return this.workshopJobService.getJobListItems(
          filter.search,
          filter.status,
          filter.customer,
          (page?.pageIndex ?? 0) + 1,
          page?.pageSize ?? 25,
          sort,
          false
        );
      })
    );
    // Watched for paged data changes and send the result to the table to render.
    this._renderChangesSubscription?.unsubscribe();
    this._renderChangesSubscription = paginatedData.subscribe(
      ({ data, count, statusCounts }) => {
        this.store.setStatusCounts(statusCounts);
        this.matPaginator.length = count;
        this._myRenderData.next(data);
      }
    );
  }
}
